import type { CatalogFile } from '../../shared/catalog-types';

export interface ResolvedPhoto {
  id: string;
  title: string;
  filename: string;
}

/** A node in the virtual folder tree derived from the "/"-delimited folder paths. */
export interface FolderNode {
  /** Raw URL-safe path segment, e.g. "new-zealand". Empty string for the root. */
  name: string;
  /** Human-facing display name, e.g. "New Zealand". */
  label: string;
  /** Full path, e.g. "travel/new-zealand". Empty string for the synthetic root. */
  path: string;
  children: FolderNode[];
  /** True if this exact path is a real catalog folder (has its own photo list). */
  selectable: boolean;
}

/** Turn a URL-safe segment into a readable fallback label: "new-zealand" → "New Zealand". */
export function prettifySegment(segment: string): string {
  return segment
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\p{L}/gu, (c) => c.toUpperCase());
}

export async function fetchCatalog(signal?: AbortSignal): Promise<CatalogFile> {
  // `no-cache` forces the browser to revalidate with the server on every load
  // (cheap — it gets a 304 when unchanged) instead of serving a stale-while-
  // revalidate copy. Guarantees an edited catalog shows up on the next refresh.
  const res = await fetch('/api/catalog', { cache: 'no-cache', signal });
  if (!res.ok) {
    throw new Error(`Failed to load catalog (${res.status})`);
  }
  const data = (await res.json()) as CatalogFile;
  if (!data || typeof data !== 'object' || !Array.isArray(data.folders) || !data.photos) {
    throw new Error('Catalog has an unexpected shape.');
  }
  return data;
}

/**
 * Build the navigable folder tree. Intermediate path segments that are not
 * themselves catalog folders (e.g. "Travel" when only "Travel/Japan" exists)
 * become non-selectable grouping nodes.
 */
export function buildTree(catalog: CatalogFile): FolderNode {
  const root: FolderNode = { name: '', label: '', path: '', children: [], selectable: false };
  const realPaths = new Set(catalog.folders.map((f) => f.path));
  // Explicit labels, keyed by full path. Only real (selectable) folders can carry one;
  // intermediate grouping segments fall back to a prettified segment.
  const labelByPath = new Map(
    catalog.folders.filter((f) => f.label).map((f) => [f.path, f.label as string]),
  );

  for (const folder of catalog.folders) {
    const segments = folder.path.split('/').filter(Boolean);
    let node = root;
    let accumulated = '';
    for (const segment of segments) {
      accumulated = accumulated ? `${accumulated}/${segment}` : segment;
      let child = node.children.find((c) => c.name === segment);
      if (!child) {
        child = {
          name: segment,
          label: labelByPath.get(accumulated) ?? prettifySegment(segment),
          path: accumulated,
          children: [],
          selectable: realPaths.has(accumulated),
        };
        node.children.push(child);
      }
      node = child;
    }
  }

  // Children are kept in the order folders first appear in catalog.json
  // (no alphabetical sort) so the on-screen order matches the file.
  return root;
}

/** Labels for each segment of a path, for a readable breadcrumb (e.g. ["Travel", "New Zealand"]). */
export function labeledBreadcrumb(tree: FolderNode, path: string): string[] {
  const segments = path.split('/').filter(Boolean);
  const labels: string[] = [];
  let node: FolderNode | undefined = tree;
  for (const segment of segments) {
    const child: FolderNode | undefined = node?.children.find((c) => c.name === segment);
    labels.push(child ? child.label : prettifySegment(segment));
    node = child;
  }
  return labels;
}

/** Resolve a folder's ordered photo ids into full photo objects, skipping unknown ids. */
export function photosForFolder(catalog: CatalogFile, path: string): ResolvedPhoto[] {
  const folder = catalog.folders.find((f) => f.path === path);
  if (!folder) return [];
  const out: ResolvedPhoto[] = [];
  for (const id of folder.order) {
    const photo = catalog.photos[id];
    if (photo) out.push({ id, title: photo.title, filename: photo.filename });
  }
  return out;
}

/** The first selectable folder, used as the default landing folder. */
export function firstSelectableFolder(node: FolderNode): string | null {
  if (node.selectable && node.path) return node.path;
  for (const child of node.children) {
    const found = firstSelectableFolder(child);
    if (found) return found;
  }
  return null;
}
